import { useMemo, useState } from "react";
import { useBoard, boardStore, otherEnd } from "@/lib/boardStore";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Check, X, Pencil } from "lucide-react";
import { toast } from "sonner";

const YARN_COLORS = [
  { name: "Red", value: "#DC2626" },
  { name: "White", value: "#F4EBD8" },
  { name: "Yellow", value: "#EAB308" },
  { name: "Blue", value: "#3B82F6" },
  { name: "Green", value: "#22C55E" },
  { name: "Black", value: "#111111" },
];

function ConnectionRow({ conn, other, editing, draft, setDraft, setEditing }) {


  const startEdit = () => {
    setDraft(conn.label || "");
    setEditing(conn.id);
  };
  const save = () => {
    boardStore.updateConnection(conn.id, { label: draft.trim() });
    setEditing(null);
    setDraft("");
    toast("Label updated");
  };
  const cancel = () => {
    setEditing(null);
    setDraft("");
  };

  return (
    <div
      data-testid={`connection-row-${conn.id}`}
      className="paper-tape rounded-sm px-3 py-2 flex items-start gap-2 text-[#1a1512]"
      style={{ borderLeft: `4px solid ${conn.color || "#DC2626"}` }}
    >
      <div className="flex-1 min-w-0">
        <div className="typewriter text-sm truncate">
          → {other.text || "(untitled)"}
        </div>

        {editing === conn.id ? (
          <div className="mt-1 space-y-1">
            <Textarea
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") cancel();
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) save();
              }}
              data-testid={`edit-label-input-${conn.id}`}
              placeholder="Label…"
              className="bg-[#fdf6de] border-[#5c4426]/40 text-[#1a1512] handwritten text-base min-h-[52px] placeholder:text-[#5c4426]/40"
              maxLength={200}
            />
            <div className="flex justify-end gap-1">
              <button
                onClick={cancel}
                data-testid={`edit-label-cancel-${conn.id}`}
                className="typewriter text-[10px] px-2 py-1 rounded text-[#5c4426] hover:bg-[#5c4426]/15"
              >
                Cancel
              </button>
              <button
                onClick={save}
                data-testid={`edit-label-save-${conn.id}`}
                className="typewriter text-[10px] px-2 py-1 rounded bg-[#c9702a] hover:bg-[#e08432] text-[#1a140c] font-semibold"
              >
                Save
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={startEdit}
            data-testid={`edit-label-btn-${conn.id}`}
            title="Edit label"
            className="mt-0.5 group inline-flex items-start gap-1.5 text-left w-full hover:bg-[#f6efc7]/40 rounded px-1 py-0.5 -mx-1 transition-colors"
          >
            {conn.label ? (
              <span
                className="handwritten text-base leading-tight flex-1"
                style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
              >
                “{conn.label}”
              </span>
            ) : (
              <span className="typewriter text-[11px] italic text-[#5c4426]/60 flex-1">
                (no label — click to add)
              </span>
            )}
            <Pencil
              size={11}
              className="mt-1 text-[#5c4426]/60 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
            />
          </button>
        )}

        {(conn.pins || []).length > 0 && (
          <div className="typewriter text-[10px] text-[#5c4426]/80 mt-0.5">
            {(conn.pins || []).length} bend
            {(conn.pins || []).length === 1 ? "" : "s"}
          </div>
        )}
      </div>

      <div className="flex flex-col items-center gap-1 pt-0.5">
        <input
          type="color"
          value={conn.color || "#DC2626"}
          onChange={(e) =>
            boardStore.updateConnection(conn.id, { color: e.target.value })
          }
          data-testid={`connection-color-${conn.id}`}
          title="Yarn color"
          className="w-6 h-6 rounded cursor-pointer border-0 bg-transparent p-0"
        />
        <button
          onClick={() => {
            boardStore.removeConnection(conn.id);
            toast("Thread cut");
          }}
          data-testid={`remove-connection-${conn.id}`}
          title="Cut the yarn"
          className="text-[#8b1a1a] hover:text-red-600"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

export default function ConnectionEditor({ cardId }) {
  const { cards, connections } = useBoard();
  const [adding, setAdding] = useState(false);
  const [targetId, setTargetId] = useState("");
  const [label, setLabel] = useState("");
  const [color, setColor] = useState("#DC2626");
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");
  const cardConnections = useMemo(
    () => connections.filter((c) => c.a === cardId || c.b === cardId),
    [connections, cardId]
  );

  const connectedIds = useMemo(
    () => new Set(cardConnections.map((c) => otherEnd(c, cardId))),
    [cardConnections, cardId]
  );

  const availableTargets = useMemo(
    () => cards.filter((c) => c.id !== cardId && !connectedIds.has(c.id)),
    [cards, cardId, connectedIds]
  );

  const cardById = (id) => cards.find((c) => c.id === id);

  const startAdd = () => {
    if (availableTargets.length === 0) {
      toast("No available cards to connect");
      return;
    }
    setAdding(true);
    setTargetId(availableTargets[0].id);
    setLabel("");
    setColor("#DC2626");
  };

  const confirmAdd = () => {
    if (!targetId) return;
    const res = boardStore.addConnection(cardId, targetId, label.trim(), color);
    if (res) {
      toast("Connection strung");
      setAdding(false);
      setTargetId("");
      setLabel("");
    } else {
      toast.error("Connection already exists");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="noir-title text-lg text-[#f4d9a8]">
            Connections
          </div>
          <div className="typewriter text-[10px] uppercase tracking-widest text-[#c9b285]/60">
            {cardConnections.length} thread
            {cardConnections.length === 1 ? "" : "s"}
          </div>
        </div>
        <Button
          size="sm"
          onClick={startAdd}
          disabled={adding || availableTargets.length === 0}
          data-testid="add-connection-btn"
          className="bg-[#c9702a] hover:bg-[#e08432] text-[#1a140c] typewriter text-xs disabled:opacity-40"
        >
          <Plus size={14} className="mr-1" /> Add
        </Button>
      </div>

      <div className="space-y-2">
        {cardConnections.map((conn) => {
          const other = cardById(otherEnd(conn, cardId));
          if (!other) return null;

          return ConnectionRow({ conn, other, editing, draft, setDraft, setEditing });


          // return (
          //   <div
          //     key={conn.id}
          //     data-testid={`connection-row-${conn.id}`}
          //     className="paper-tape rounded-sm px-3 py-2 flex items-center gap-2 text-[#1a1512]"
          //     style={{ borderLeft: `4px solid ${conn.color || "#DC2626"}` }}
          //   >
          //     <div className="flex-1 min-w-0">
          //       <div className="typewriter text-sm truncate">
          //         → {other.text || "(untitled)"}
          //       </div>
          //       {conn.label && (
          //         <div
          //           className="handwritten text-base leading-tight"
          //           style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
          //         >
          //           “{conn.label}”
          //         </div>
          //       )}
          //       {(conn.pins || []).length > 0 && (
          //         <div className="typewriter text-[10px] text-[#5c4426]/80 mt-0.5">
          //           {(conn.pins || []).length} bend{(conn.pins || []).length === 1 ? "" : "s"}
          //         </div>
          //       )}
          //     </div>
          //     <input
          //       type="color"
          //       value={conn.color || "#DC2626"}
          //       onChange={(e) =>
          //         boardStore.updateConnection(conn.id, { color: e.target.value })
          //       }
          //       data-testid={`connection-color-${conn.id}`}
          //       title="Yarn color"
          //       className="w-6 h-6 rounded cursor-pointer border-0 bg-transparent p-0"
          //     />
          //     <button
          //       onClick={() => {
          //         boardStore.removeConnection(conn.id);
          //         toast("Thread cut");
          //       }}
          //       data-testid={`remove-connection-${conn.id}`}
          //       title="Cut the yarn"
          //       className="text-[#8b1a1a] hover:text-red-600"
          //     >
          //       <Trash2 size={14} />
          //     </button>
          //   </div>
          // );
        })}
        {cardConnections.length === 0 && !adding && (
          <div className="typewriter text-[#c9b285]/50 text-xs italic">
            No threads yet. String this node to another.
          </div>
        )}
      </div>

      {adding && (
        <div
          data-testid="new-connection-form"
          className="border border-dashed border-[#5c4426]/60 rounded p-3 space-y-2 bg-[#1a140c]/60"
        >
          <div className="typewriter text-[10px] uppercase tracking-widest text-[#c9b285]">
            Connect to
          </div>
          <Select value={targetId} onValueChange={setTargetId}>
            <SelectTrigger
              data-testid="connection-target-select"
              className="bg-[#0f0b07] border-[#5c4426]/50 text-[#f4ebd8] typewriter"
            >
              <SelectValue placeholder="Pick a card…" />
            </SelectTrigger>
            <SelectContent className="bg-[#1a140c] border-[#5c4426]/50 text-[#f4ebd8]">
              {availableTargets.map((c) => (
                <SelectItem
                  key={c.id}
                  value={c.id}
                  data-testid={`connection-target-option-${c.id}`}
                  className="typewriter focus:bg-[#5c4426]/40 focus:text-[#f4ebd8]"
                >
                  {c.text || "(untitled)"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea
            data-testid="connection-label-input"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={"Label (optional):\n'saw at gala'\n'phone records match'"}
            className="bg-[#0f0b07] border-[#5c4426]/50 text-[#f4ebd8] typewriter placeholder:text-[#c9b285]/40 text-sm min-h-[64px]"
            maxLength={200}
          />
          <div className="typewriter text-[10px] text-[#c9b285]/60">
            Enter for a new line
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            <span className="typewriter text-[10px] uppercase tracking-widest text-[#c9b285] mr-1">
              Yarn:
            </span>
            {YARN_COLORS.map((yc) => (
              <button
                key={yc.value}
                onClick={() => setColor(yc.value)}
                data-testid={`yarn-color-${yc.name.toLowerCase()}`}
                className={`w-6 h-6 rounded-full border-2 ${color === yc.value
                    ? "border-[#f4d9a8]"
                    : "border-[#5c4426]/40"
                  }`}
                style={{ background: yc.value }}
                title={yc.name}
              />
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setAdding(false)}
              data-testid="cancel-connection-btn"
              className="text-[#c9b285] hover:bg-[#5c4426]/30 typewriter text-xs"
            >
              <X size={12} className="mr-1" /> Cancel
            </Button>
            <Button
              size="sm"
              onClick={confirmAdd}
              data-testid="confirm-connection-btn"
              className="bg-[#c9702a] hover:bg-[#e08432] text-[#1a140c] typewriter text-xs"
            >
              <Check size={12} className="mr-1" /> Connect
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
